import React, { FC, useContext, useRef, useState } from 'react'
import useOnClickOutside from '../../../hooks/useOnClickOutside'
import {
  YMaps,
  Map as MapComponent,
  Placemark,
  YMapsApi,
} from 'react-yandex-maps'
import 'react-perfect-scrollbar/dist/css/styles.css'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { WheretoBuyContext } from '../../../contexts/whereToBuyProvider'
import { ICity, IShop } from '../../types'
import Shop from '../Shop/Shop'
import css from './Map.module.scss'
import CloseIcon from '../../../public/static/icons/close.svg'
import PlusIcon from '../../../public/static/icons/plus-big.svg'
import MinusIcon from '../../../public/static/icons/minus-big.svg'
import NavIcon from '../../../public/static/icons/nav.svg'
import cx from 'classnames'

const Map: FC = () => {
  const [zoom, setZoom] = useState(4)
  const { cities, mapCenter, setMapCenter } = useContext(WheretoBuyContext)
  const [globalymaps, setGlobalYmaps] = useState<YMapsApi>(null)
  const allShops = []
  const [aciveShop, setActiveShop] = useState<IShop>(null)
  const [activeCity, setActiveCity] = useState<ICity>(null)
  const [showSide, setShowSide] = useState(false)
  const asideRef = useRef(null)

  for (let i = 0; i < cities.length; i++) {
    cities[i].shops.map((shop) => {
      allShops.push(shop)
    })
  }

  useOnClickOutside(asideRef, () => {
    setShowSide(false)
    setActiveCity(null)
    setActiveShop(null)
  })

  let mapRef
  const findNearestShop = async () => {
    try {
      const objects = await globalymaps.geolocation.get({
        provider: 'browser',
        autoReverseGeocode: false,
      })

      const userCoodinates = objects.geoObjects.get(0).geometry.getCoordinates()

      const coordSystem = mapRef.options.get('projection').getCoordSystem()

      let minDist = coordSystem.getDistance(userCoodinates, [
        +allShops[0].coords.lat,
        +allShops[0].coords.lng,
      ])
      let closestPointIdx = 0

      for (let i = 1, l = allShops.length; i < l; ++i) {
        const dist = coordSystem.getDistance(userCoodinates, [
          +allShops[i].coords.lat,
          +allShops[i].coords.lng,
        ])
        if (minDist > dist) {
          minDist = dist
          closestPointIdx = i
        }
      }

      await mapRef.panTo(
        [
          +allShops[closestPointIdx].coords.lat,
          +allShops[closestPointIdx].coords.lng,
        ],
        {
          checkZoomRange: true,
        },
      )

      setZoom(15)
      setMapCenter([
        +allShops[closestPointIdx].coords.lat,
        +allShops[closestPointIdx].coords.lng,
      ])
    } catch (error) {
      console.error(error)
    }
  }

  const ZoomButtons = () => {
    return (
      <div className={css.zoom_buttons}>
        <button
          className={css.zoom_plus}
          type="button"
          onClick={() =>
            setZoom((prev) => {
              if (prev >= 20) {
                return prev
              }
              return prev + 1
            })
          }
        >
          <PlusIcon />
        </button>
        <button
          className={css.zoom_minus}
          type="button"
          onClick={() =>
            setZoom((prev) => {
              if (prev === 0) {
                return prev
              }
              return prev - 1
            })
          }
        >
          <MinusIcon />
        </button>
      </div>
    )
  }

  const NavButton = () => {
    return (
      <button
        type="button"
        className={css.nav_button}
        onClick={() => findNearestShop()}
      >
        <NavIcon />
      </button>
    )
  }

  return (
    <section className={css.map} id="contacts-map">
      <YMaps>
        <MapComponent
          onLoad={(ymaps) => setGlobalYmaps(ymaps)}
          state={{
            center: mapCenter,
            zoom,
            behaviors: ['drag', 'multiTouch'],
          }}
          modules={['geolocation']}
          width="100%"
          height={745}
          instanceRef={(ref) => (mapRef = ref)}
        >
          {cities.map((city) =>
            zoom <= 7
              ? city.coords &&
                city.shops.length > 0 && (
                  <Placemark
                    key={`placemark-${city.code}`}
                    geometry={[+city.coords.lat, +city.coords.lng]}
                    options={{
                      iconLayout: 'default#image',
                      iconImageHref:
                        activeCity?.code === city.code
                          ? '/static/icons/placemark-ymap-active.svg'
                          : '/static/icons/placemark-ymap.svg',
                    }}
                    onClick={() => {
                      setShowSide(true)
                      setActiveCity(city)
                    }}
                  />
                )
              : city.shops.map((shop) => (
                  <Placemark
                    key={`placemark-${shop.code}`}
                    geometry={[+shop.coords.lat, +shop.coords.lng]}
                    options={{
                      iconLayout: 'default#image',
                      iconImageHref:
                        aciveShop?.code === shop.code
                          ? '/static/icons/placemark-ymap-active.svg'
                          : '/static/icons/placemark-ymap.svg',
                    }}
                    onClick={() => {
                      setShowSide(true)
                      setActiveShop(shop)
                    }}
                  />
                )),
          )}
        </MapComponent>
        <ZoomButtons />
        <NavButton />
        <div className={cx(css.side, showSide && css.active)} ref={asideRef}>
          <PerfectScrollbar options={{ maxScrollbarLength: 200 }}>
            <div
              className={cx(
                css.inner,
                zoom <= 7 && activeCity?.shops.length > 1 && css.block,
              )}
            >
              <button
                className={css.close}
                onClick={() => {
                  setShowSide(false)
                  setActiveCity(null)
                  setActiveShop(null)
                }}
                type="button"
              >
                <CloseIcon />
              </button>
              {zoom <= 7 ? (
                <ul className={css.cities}>
                  {activeCity?.shops?.map((shop, index) => (
                    <Shop
                      shop={shop}
                      key={shop.code}
                      border={index < activeCity?.shops.length - 1}
                    />
                  ))}
                </ul>
              ) : (
                <Shop shop={aciveShop} />
              )}
            </div>
          </PerfectScrollbar>
        </div>
      </YMaps>
    </section>
  )
}

export default Map
